import { FC, useState } from "react";
import Table from "react-bootstrap/Table";
import Button from "react-bootstrap/Button";
import Alert from "react-bootstrap/Alert";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import ButtonGroup from "react-bootstrap/ButtonGroup";
import { Link } from "react-router-dom";
import { FaEdit, FaTrash } from "react-icons/fa";
import { mapNodes } from "app/util/graph";
import { useTestTypePrerequisiteListQuery, TestTypePrerequisiteFieldsFragment } from "app/api/graph/types";
import { LoadingSpinner } from "app/component/util/LoadingSpinner";
import {
    testTypePrerequisiteRoute,
    deleteTestTypePrerequisiteRoute,
    editTestTypePrerequisiteRoute,
} from "app/route/Routes";
import { SortableTableHeader, SortDirection } from "app/component/table/SortableTableHeader";
import {
    TestTypePrerequisitesTableFiltersForm,
    FormValues,
} from "app/component/test-type-prerequisite/TestTypePrerequisitesTableFiltersForm";
import { BlockButtonGroup } from "app/component/button/BlockButtonGroup";
import { ButtonLink } from "app/component/button/ButtonLink";
import { useGeneralPermissions } from "app/api/graph/hook/use-general-permissions";
import { testTypeLabel } from "app/api/graph/strings";

type TableRowProps = {
    testTypePrerequisite: TestTypePrerequisiteFieldsFragment;
};

const TableRow = ({ testTypePrerequisite }: TableRowProps) => {
    const permissions = useGeneralPermissions(testTypePrerequisite.permissions);

    return (
        <tr>
            <td>{testTypeLabel(testTypePrerequisite.testType)}</td>
            <td>
                <Link to={testTypePrerequisiteRoute.build({ testTypePrerequisiteId: testTypePrerequisite._id })}>
                    {testTypePrerequisite.description}
                </Link>
            </td>
            <td>
                {permissions.canUpdate ? (
                    <ButtonGroup>
                        <ButtonLink
                            to={editTestTypePrerequisiteRoute.build({
                                testTypePrerequisiteId: testTypePrerequisite._id,
                            })}
                            variant="warning"
                            size="sm"
                            title="edit"
                        >
                            <FaEdit />
                        </ButtonLink>
                    </ButtonGroup>
                ) : null}
                {permissions.canDelete ? (
                    <ButtonGroup>
                        <ButtonLink
                            to={deleteTestTypePrerequisiteRoute.build({
                                testTypePrerequisiteId: testTypePrerequisite._id,
                            })}
                            variant="danger"
                            size="sm"
                            title="Delete"
                        >
                            <FaTrash />
                        </ButtonLink>
                    </ButtonGroup>
                ) : null}
            </td>
        </tr>
    );
};

export const TestTypePrerequisitesTable: FC = () => {
    const [descriptionOrder, setDescriptionOrder] = useState<SortDirection>(undefined);
    const [testTypeOrder, setTestTypeOrder] = useState<SortDirection>(undefined);
    const [filters, setFilters] = useState<FormValues>({
        testType: undefined,
    });
    const { data, loading, error, fetchMore } = useTestTypePrerequisiteListQuery({
        variables: { order: { description: descriptionOrder, testType: testTypeOrder }, ...filters },
        notifyOnNetworkStatusChange: true,
    });
    const testTypePrerequisites = data?.testTypePrerequisites;

    if (loading && !testTypePrerequisites) {
        return <LoadingSpinner />;
    }
    if (error) {
        return <Alert variant="danger">{error.message}</Alert>;
    }
    if (!testTypePrerequisites) {
        return <Alert variant="danger">Not found.</Alert>;
    }

    const fetchMoreTestTypePrerequisites = () =>
        fetchMore({ variables: { after: testTypePrerequisites.pageInfo.endCursor } });

    return (
        <Row>
            <Col>
                <Table responsive="sm" bordered className="crud-table">
                    <thead>
                        <tr>
                            <SortableTableHeader direction={testTypeOrder} onChange={setTestTypeOrder}>
                                Test type
                            </SortableTableHeader>
                            <SortableTableHeader direction={descriptionOrder} onChange={setDescriptionOrder}>
                                Description
                            </SortableTableHeader>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {mapNodes(testTypePrerequisites, (testTypePrerequisite) => (
                            <TableRow key={testTypePrerequisite.id} testTypePrerequisite={testTypePrerequisite} />
                        ))}
                        {testTypePrerequisites.edges?.length === 0 ? (
                            <tr>
                                <td colSpan={100}>No prerequisites.</td>
                            </tr>
                        ) : null}
                        {loading ? (
                            <tr>
                                <td colSpan={100}>
                                    <LoadingSpinner />
                                </td>
                            </tr>
                        ) : null}
                        {testTypePrerequisites.pageInfo.hasNextPage ? (
                            <tr>
                                <td colSpan={100}>
                                    <BlockButtonGroup>
                                        <Button variant="light" onClick={fetchMoreTestTypePrerequisites}>
                                            Load more
                                        </Button>
                                    </BlockButtonGroup>
                                </td>
                            </tr>
                        ) : null}
                    </tbody>
                </Table>
            </Col>
            <Col md={3}>
                <TestTypePrerequisitesTableFiltersForm initialValues={filters} onSubmit={setFilters} />
            </Col>
        </Row>
    );
};
